import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Input, InputNumber, Row, Col, Typography, Button, Spin, Tag, App, Table, Switch, Tooltip } from 'antd';
import { TeamOutlined, CheckCircleOutlined, PlusOutlined, DeleteOutlined, GiftOutlined, AmazonOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { readCalculatorDraft, writeCalculatorDraft, type CalculatorDraftDims } from './calculatorDraft';
import {
  fetchKeepaData,
  fetchCompetitors,
  type KeepaProductData,
  type CompetitorData,
  type NicheData,
  type ProductCardFields,
} from '../../shared/api/client';
import { numericInputProps } from '../../shared/lib/formatters';
import { useAppColors } from '../../shared/theme';
import CompetitorModal from './CompetitorModal';
import {
  computeProductCardUpdate,
  productCardUpdateHasChanges,
  type ProductCardApplyResult,
} from './productCardApply';

export interface CalculatorData {
  purchasePrice: number;
  shippingCostPerKg: number;
  unitWeightKg: number;
  shippingPrice: number;
  storageFee: number;
  sellingPrice: number;
  referralFee: number;
  fbaFee: number;
  otherFees: number;
  ppcCost: number;
  conversionRate: number;
  ppcSalesPercentage: number;
  returnRate: number;
  resellableReturns: number;
  monthlySales: number;
  batchQuantity: number;
  couponPromoEnabled: boolean;
  asin?: string;
  competitorAsins?: string[];
  keepaLoaded?: boolean;
  /**
   * Габариты в дюймах. Уходят наружу, потому что из них считается объёмный вес,
   * а из него — стоимость логистики в COGS. Пока их не было в сохраняемом
   * расчёте, открытие записи из истории давало другую себестоимость, чем при
   * сохранении, и ничего об этом не сообщало.
   */
  dims?: CalculatorDraftDims;
}

interface CalculatorProps {
  onDataChange?: (data: CalculatorData) => void;
  initialData?: Partial<CalculatorData> & { asin?: string };
  /** Карточка товара платформы — есть только в embed, после её загрузки в DashboardView. */
  productCard?: ProductCardFields | null;
}

function defaultBatch(monthlySales: number): number {
  return Math.max(100, Math.ceil(monthlySales * 3));
}

const Calculator: React.FC<CalculatorProps> = ({ onDataChange, initialData, productCard }) => {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const c = useAppColors();

  // Track if user has made changes (dirty flag to prevent unwanted resets)
  const isDirtyRef = useRef(false);
  // Track the last applied initialData to avoid re-applying same data
  const appliedInitialRef = useRef<string | null>(null);
  // Track the last applied productCard to avoid re-applying/re-asking on every render
  const appliedProductCardRef = useRef<ProductCardFields | null>(null);
  // Track the last Keepa price to detect manual user changes
  const keepaPriceRef = useRef<number | null>(null);
  // Track if user manually changed the price
  const priceManuallyChangedRef = useRef(false);
  // Track the last ASIN that was looked up via Keepa
  const lastLookedUpAsinRef = useRef<string | null>(null);
  // Запрос Keepa, который сейчас в полёте. Пользователь вводит ASIN-1, не дожидается
  // и вводит ASIN-2 — ответ по ASIN-1 приходит вторым и без отмены перезаписывает
  // цену, комиссии, продажи, вес и габариты ЧУЖОГО товара, а `keepaLoaded` объявляет
  // их достоверными: в поле ASIN-2, в цифрах ASIN-1, и ничем это не помечено.
  // Приём тот же, что в widgets/competitors/CompetitorAnalysis.tsx.
  const keepaAbortRef = useRef<AbortController | null>(null);
  // Вопрос «оставить вашу цену или взять цену листинга?» принадлежит конкретному
  // запросу: как только пользователь ушёл искать следующий ASIN, подтверждать
  // цену предыдущего не по чему — окно закрывается вместе с отменой запроса.
  const priceConfirmRef = useRef<{ destroy: () => void } | null>(null);

  // Ремоут размонтируют, пока запрос в полёте (смена карточки, уход со страницы
  // платформы): без этого повисло бы окно подтверждения цены поверх интерфейса
  // хоста, уже ни к какому дереву не привязанное.
  useEffect(() => () => {
    keepaAbortRef.current?.abort();
    priceConfirmRef.current?.destroy();
  }, []);

  // Черновик читается ОДИН раз на монтирование: он принадлежит владельцу,
  // названному при mount'е, и внутри жизни дерева смениться не может.
  const [draft] = useState(readCalculatorDraft);

  const [asin, setAsin] = useState<string>(draft?.asin || '');
  const [keepaLoading, setKeepaLoading] = useState(false);
  const [keepaLoaded, setKeepaLoaded] = useState<boolean>(draft?.keepaLoaded || false);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorAsins, setCompetitorAsins] = useState<string[]>(draft?.competitorAsins || []);
  const [selectedCompetitors, setSelectedCompetitors] = useState<CompetitorData[]>([]);
  const [manualCompetitorAsin, setManualCompetitorAsin] = useState('');
  const [competitorModalOpen, setCompetitorModalOpen] = useState(false);
  const [competitorsList, setCompetitorsList] = useState<CompetitorData[]>([]);
  const [competitorsNiches, setCompetitorsNiches] = useState<NicheData[]>([]);
  const [competitorsNicheTitle, setCompetitorsNicheTitle] = useState<string>('');
  const [competitorsTotalFound, setCompetitorsTotalFound] = useState<number>(0);
  const [competitorsLoadedForAsin, setCompetitorsLoadedForAsin] = useState<string>('');
  const [batchManual, setBatchManual] = useState(false);
  const [weightFromKeepa, setWeightFromKeepa] = useState(false);
  const [dimsFromKeepa, setDimsFromKeepa] = useState(false);
  const [couponPromoEnabled, setCouponPromoEnabled] = useState<boolean>(
    draft?.couponPromoEnabled ?? true,
  );

  // Dimensions in inches for manual entry
  const [dims, setDims] = useState<CalculatorDraftDims>(
    draft?.dims || { l: null, w: null, h: null },
  );

  // Calculate volumetric weight: (L × W × H) / 139 = lbs, then convert to kg
  const volumetricWeightKg = (dims.l && dims.w && dims.h)
    ? +((dims.l * dims.w * dims.h) / 139 / 2.205).toFixed(3)
    : null;

  // Data state type
  type CalcDataState = {
    purchasePrice: number;
    shippingCostPerKg: number;
    unitWeightKg: number;
    storageFee: number;
    sellingPrice: number;
    referralFee: number;
    fbaFee: number;
    otherFees: number;
    ppcCost: number;
    conversionRate: number;
    ppcSalesPercentage: number;
    returnRate: number;
    resellableReturns: number;
    monthlySales: number;
    batchQuantity: number;
  };

  // Try to restore from localStorage on first render
  const getInitialData = (): CalcDataState => {
    const defaults: CalcDataState = {
      purchasePrice: 0,
      shippingCostPerKg: 1.3,
      unitWeightKg: 0.5,
      storageFee: 0.1,
      sellingPrice: 35.59,
      referralFee: 15,
      fbaFee: 8.36,
      otherFees: 0,
      ppcCost: 0,
      conversionRate: 0,
      ppcSalesPercentage: 0,
      returnRate: 0,
      resellableReturns: 75,
      monthlySales: 0,
      batchQuantity: 400,
    };
    // Каст, а не проверка поля за полем: черновик — это разобранный JSON, и
    // недостающие/чужие ключи перекрываются `defaults` при слиянии.
    return { ...defaults, ...(draft?.data as Partial<CalcDataState> | undefined) };
  };

  const [data, setData] = useState<CalcDataState>(getInitialData);

  const [cnyRate, setCnyRate] = useState(7.0); // CNY per 1 USD
  const [rateLoading, setRateLoading] = useState(true);
  const [purchasePriceCny, setPurchasePriceCny] = useState<number | null>(
    draft?.purchasePriceCny ?? null,
  );

  // Fetch exchange rate on mount
  useEffect(() => {
    fetch('https://api.fxratesapi.com/latest?base=USD&symbols=CNY')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.rates?.CNY) {
          setCnyRate(+data.rates.CNY.toFixed(4));
        }
      })
      .catch(() => {
        // Keep default rate on error
      })
      .finally(() => setRateLoading(false));
  }, []);

  // Convert CNY to USD when CNY changes
  const handleCnyChange = (cny: number | null) => {
    isDirtyRef.current = true;
    setPurchasePriceCny(cny);
    if (cny !== null && cny > 0 && cnyRate > 0) {
      const usd = +(cny / cnyRate).toFixed(2);
      setData((prev) => ({ ...prev, purchasePrice: usd }));
    }
  };

  // Convert USD to CNY when USD changes (sync back)
  const handleUsdChange = (usd: number | null) => {
    isDirtyRef.current = true;
    handleChange('purchasePrice', usd);
    if (usd !== null && usd > 0 && cnyRate > 0) {
      setPurchasePriceCny(+(usd * cnyRate).toFixed(2));
    } else {
      setPurchasePriceCny(null);
    }
  };

  // Recalculate USD when rate changes
  const handleRateChange = (rate: number | null) => {
    isDirtyRef.current = true;
    const newRate = rate ?? 7.26;
    setCnyRate(newRate);
    if (purchasePriceCny !== null && purchasePriceCny > 0 && newRate > 0) {
      const usd = +(purchasePriceCny / newRate).toFixed(2);
      setData((prev) => ({ ...prev, purchasePrice: usd }));
    }
  };

  // Billable weight is the greater of actual weight and volumetric weight
  const billableWeightKg = volumetricWeightKg != null
    ? Math.max(data.unitWeightKg, volumetricWeightKg)
    : data.unitWeightKg;
  const shippingPrice = +(data.shippingCostPerKg * billableWeightKg).toFixed(2);
  const cogs = data.purchasePrice + shippingPrice;

  useEffect(() => {
    const out: CalculatorData = {
      purchasePrice: data.purchasePrice,
      shippingCostPerKg: data.shippingCostPerKg,
      unitWeightKg: data.unitWeightKg,
      shippingPrice,
      storageFee: data.storageFee,
      sellingPrice: data.sellingPrice,
      referralFee: data.referralFee,
      fbaFee: data.fbaFee,
      otherFees: data.otherFees,
      ppcCost: data.ppcCost,
      conversionRate: data.conversionRate,
      ppcSalesPercentage: data.ppcSalesPercentage,
      returnRate: data.returnRate,
      resellableReturns: data.resellableReturns,
      monthlySales: data.monthlySales,
      batchQuantity: data.batchQuantity,
      couponPromoEnabled,
      keepaLoaded,
    };
    if (dims.l != null && dims.w != null && dims.h != null) out.dims = dims;
    if (asin) out.asin = asin;
    if (competitorAsins.length) out.competitorAsins = competitorAsins;
    onDataChange?.(out);
  }, [data, asin, competitorAsins, dims, couponPromoEnabled, keepaLoaded]);

  // Черновик переживает перезагрузку страницы и размонтирование ремоута; кому
  // он принадлежит и кому будет виден — решает calculatorDraft.ts.
  useEffect(() => {
    if (!isDirtyRef.current) return; // Only save user edits, not initial/loaded data
    writeCalculatorDraft({
      data,
      asin,
      competitorAsins,
      dims,
      couponPromoEnabled,
      purchasePriceCny,
      keepaLoaded,
      timestamp: Date.now(),
    });
  }, [data, asin, competitorAsins, dims, couponPromoEnabled, purchasePriceCny, keepaLoaded]);

  useEffect(() => {
    // Skip if user has made edits
    if (isDirtyRef.current) return;
    if (!initialData) return;

    // Skip if this is the same data we already applied (compare by value)
    const serialized = JSON.stringify(initialData);
    if (appliedInitialRef.current === serialized) return;

    appliedInitialRef.current = serialized;
    setData((prev) => ({
      ...prev,
      ...(initialData.purchasePrice != null && { purchasePrice: initialData.purchasePrice }),
      ...(initialData.shippingCostPerKg != null && { shippingCostPerKg: initialData.shippingCostPerKg }),
      ...(initialData.unitWeightKg != null && { unitWeightKg: initialData.unitWeightKg }),
      ...(initialData.storageFee != null && { storageFee: initialData.storageFee }),
      ...(initialData.sellingPrice != null && { sellingPrice: initialData.sellingPrice }),
      ...(initialData.referralFee != null && { referralFee: initialData.referralFee }),
      ...(initialData.fbaFee != null && { fbaFee: initialData.fbaFee }),
      ...(initialData.otherFees != null && { otherFees: initialData.otherFees }),
      ...(initialData.ppcCost != null && { ppcCost: initialData.ppcCost }),
      ...(initialData.conversionRate != null && { conversionRate: initialData.conversionRate }),
      ...(initialData.ppcSalesPercentage != null && { ppcSalesPercentage: initialData.ppcSalesPercentage }),
      ...(initialData.returnRate != null && { returnRate: initialData.returnRate }),
      ...(initialData.resellableReturns != null && { resellableReturns: initialData.resellableReturns }),
      ...(initialData.monthlySales != null && { monthlySales: initialData.monthlySales }),
      ...(initialData.batchQuantity != null && { batchQuantity: initialData.batchQuantity }),
    }));
    if (initialData.dims) setDims(initialData.dims);
    if (initialData.asin) setAsin(initialData.asin);
    if (initialData.competitorAsins) setCompetitorAsins(initialData.competitorAsins);
    if (initialData.couponPromoEnabled != null) setCouponPromoEnabled(initialData.couponPromoEnabled);
    // Mark keepa as loaded if we have asin and keepa-derived data (fbaFee or monthlySales)
    if (initialData.asin && (initialData.fbaFee != null || initialData.monthlySales != null)) {
      setKeepaLoaded(true);
    }
    // Recalculate CNY price from USD when loading saved data
    if (initialData.purchasePrice != null && initialData.purchasePrice > 0 && cnyRate > 0) {
      setPurchasePriceCny(+(initialData.purchasePrice * cnyRate).toFixed(2));
    }
  }, [initialData, cnyRate]);

  const handleChange = (field: keyof typeof data, value: number | null) => {
    isDirtyRef.current = true;
    setData((prev) => ({ ...prev, [field]: value ?? 0 }));
  };

  const handleMonthlySalesChange = (value: number | null) => {
    isDirtyRef.current = true;
    const ms = value ?? 0;
    setData((prev) => ({
      ...prev,
      monthlySales: ms,
      ...(!batchManual ? { batchQuantity: defaultBatch(ms) } : {}),
    }));
  };

  const handleBatchChange = (value: number | null) => {
    isDirtyRef.current = true;
    setBatchManual(true);
    setData((prev) => ({ ...prev, batchQuantity: value ?? 100 }));
  };

  // ASIN validation: only A-Z, 0-9, no spaces or Cyrillic
  const sanitizeAsin = (value: string): string => {
    return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  };

  const handleAsinChange = (value: string) => {
    isDirtyRef.current = true;
    const newAsin = sanitizeAsin(value);

    // Reset advertising/returns fields when:
    // 1. ASIN is cleared
    // 2. New complete ASIN is different from old complete ASIN (e.g., paste new ASIN)
    // 3. ASIN length decreases (partial deletion)
    const shouldReset =
      newAsin.length === 0 ||
      (asin.length === 10 && newAsin.length === 10 && newAsin !== asin) ||
      (asin.length === 10 && newAsin.length < 10);

    if (shouldReset) {
      setData((prev) => ({
        ...prev,
        ppcCost: 0,
        conversionRate: 0,
        ppcSalesPercentage: 0,
        returnRate: 0,
      }));
      setKeepaLoaded(false);
    }

    // Reset keepaLoaded when ASIN changes (even partially)
    if (newAsin !== asin) {
      setKeepaLoaded(false);
    }
    setAsin(newAsin);
  };

  const handleDimChange = (dim: 'l' | 'w' | 'h', value: number | null) => {
    isDirtyRef.current = true;
    setDims((p) => ({ ...p, [dim]: value }));
    setDimsFromKeepa(false);
  };

  const handleCouponPromoChange = (checked: boolean) => {
    isDirtyRef.current = true;
    setCouponPromoEnabled(checked);
  };

  // Track manual price changes separately
  const handleSellingPriceChange = (value: number | null) => {
    isDirtyRef.current = true;
    priceManuallyChangedRef.current = true;
    setData((prev) => ({ ...prev, sellingPrice: value ?? 0 }));
  };

  const applyKeepaData = useCallback((kd: KeepaProductData, options?: { skipPrice?: boolean }) => {
    const skipPrice = options?.skipPrice ?? false;

    setData((prev) => {
      const next = { ...prev };
      if (kd.buy_box_price != null && !skipPrice) {
        next.sellingPrice = kd.buy_box_price;
        keepaPriceRef.current = kd.buy_box_price;
        priceManuallyChangedRef.current = false; // Reset manual change flag
      }
      if (kd.fba_fee != null) next.fbaFee = kd.fba_fee;
      if (kd.referral_fee != null) next.referralFee = kd.referral_fee;
      if (kd.monthly_sales != null) {
        next.monthlySales = kd.monthly_sales;
        if (!batchManual) next.batchQuantity = defaultBatch(kd.monthly_sales);
      }
      if (kd.package_weight_kg != null) {
        next.unitWeightKg = kd.package_weight_kg;
        setWeightFromKeepa(true);
      }
      return next;
    });
    // Set dimensions from Keepa (convert cm to inches: 1 inch = 2.54 cm)
    if (kd.package_dimensions_cm?.length && kd.package_dimensions_cm?.width && kd.package_dimensions_cm?.height) {
      setDims({
        l: +(kd.package_dimensions_cm.length / 2.54).toFixed(2),
        w: +(kd.package_dimensions_cm.width / 2.54).toFixed(2),
        h: +(kd.package_dimensions_cm.height / 2.54).toFixed(2),
      });
      setDimsFromKeepa(true);
    }
  }, [batchManual]);

  // Применяет уже посчитанный computeProductCardUpdate() — сам без побочных
  // эффектов не читает/не решает ничего, только раскладывает результат по setState.
  const applyProductCard = useCallback((update: ProductCardApplyResult) => {
    if (Object.keys(update.fields).length > 0) {
      setData((prev) => ({ ...prev, ...update.fields }));
    }
    if (update.dims) setDims(update.dims);
    if (update.asin) setAsin(update.asin);
    if (update.purchasePriceCny != null) setPurchasePriceCny(update.purchasePriceCny);
    if (update.cnyRate != null) setCnyRate(update.cnyRate);
  }, []);

  // Подстановка карточки товара платформы — один раз на карточку (ref-гард ниже)
  // и БЕЗ подтверждения: данные карточки и есть точка отсчёта расчёта, а чтобы
  // вернуться к прежнему расчёту, есть история по этому же product_id. Спрашивать
  // было хуже: черновик в localStorage остаётся от прошлой сессии почти всегда,
  // и вопрос всплывал при каждом открытии модуля.
  useEffect(() => {
    if (!productCard) return;
    if (appliedProductCardRef.current === productCard) return;
    appliedProductCardRef.current = productCard;

    const update = computeProductCardUpdate(productCard, cnyRate);
    if (!productCardUpdateHasChanges(update)) return;

    applyProductCard(update);
  }, [productCard, cnyRate, applyProductCard]);

  const lookupAsin = useCallback(async (value: string) => {
    const cleaned = value.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(cleaned)) return;

    // Reset user-entered advertising fields when looking up a DIFFERENT ASIN
    if (lastLookedUpAsinRef.current && lastLookedUpAsinRef.current !== cleaned) {
      setData((prev) => ({
        ...prev,
        ppcCost: 0,
        conversionRate: 0,
        ppcSalesPercentage: 0,
        returnRate: 0,
      }));
    }
    lastLookedUpAsinRef.current = cleaned;

    keepaAbortRef.current?.abort();
    priceConfirmRef.current?.destroy();
    priceConfirmRef.current = null;
    const controller = new AbortController();
    keepaAbortRef.current = controller;
    const { signal } = controller;

    setKeepaLoading(true);
    setKeepaLoaded(false);
    setWeightFromKeepa(false);
    setDimsFromKeepa(false);
    setDims({ l: null, w: null, h: null });
    try {
      const result = await fetchKeepaData([cleaned], signal);
      // Ответ отменённого запроса не пишет НИЧЕГО: ни данных, ни `keepaLoaded`,
      // ни сообщения — им распоряжается уже следующий ASIN.
      if (signal.aborted) return;
      const kd = result[cleaned];
      if (kd && !kd.error) {
        // Check if user manually changed the price and Keepa has a different price
        const userPrice = data.sellingPrice;
        const keepaPrice = kd.buy_box_price;
        const priceWasManuallyChanged = priceManuallyChangedRef.current;
        const pricesAreDifferent = keepaPrice != null && Math.abs(userPrice - keepaPrice) > 0.01;

        if (priceWasManuallyChanged && pricesAreDifferent) {
          // Show confirmation modal
          priceConfirmRef.current = modal.confirm({
            title: t('idea_calc.calculator.modal_price_changed_title'),
            zIndex: 1200,
            content: (
              <div>
                <p>{t('idea_calc.calculator.modal_price_current')} <strong>${userPrice.toFixed(2)}</strong></p>
                <p>{t('idea_calc.calculator.modal_price_listing')} <strong>${keepaPrice?.toFixed(2)}</strong></p>
                <p>{t('idea_calc.calculator.modal_price_question')}</p>
              </div>
            ),
            okText: t('idea_calc.calculator.modal_price_ok'),
            cancelText: t('idea_calc.calculator.modal_price_cancel'),
            onOk: () => {
              priceConfirmRef.current = null;
              applyKeepaData(kd); // Apply with price update
            },
            onCancel: () => {
              priceConfirmRef.current = null;
              applyKeepaData(kd, { skipPrice: true }); // Apply without price update
            },
          });
        } else {
          // First load or price wasn't changed - apply normally
          applyKeepaData(kd);
        }
        setKeepaLoaded(true);
      } else {
        message.warning(kd?.error || t('idea_calc.calculator.msg_keepa_no_data'));
      }
    } catch (error) {
      // Отмена — не сбой: ошибку показывает только тот запрос, которого никто не
      // отменял, иначе пользователь получал бы «Keepa недоступна» ровно за то,
      // что ввёл следующий ASIN.
      if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) return;
      message.error(t('idea_calc.calculator.msg_keepa_load_error'));
    } finally {
      // Спиннер принадлежит запросу, который сейчас в полёте: гасить его ответом
      // отменённого значило бы объявить загрузку законченной посреди новой.
      if (!signal.aborted) setKeepaLoading(false);
    }
  }, [applyKeepaData, data.sellingPrice, modal, t]);

  const loadCompetitors = useCallback(async () => {
    const cleaned = asin.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(cleaned)) {
      message.warning(t('idea_calc.calculator.msg_invalid_asin'));
      return;
    }
    // If competitors already loaded for this ASIN, just open modal
    if (competitorsLoadedForAsin === cleaned && (competitorsNiches.length > 0 || competitorsList.length > 0)) {
      setCompetitorModalOpen(true);
      return;
    }
    setCompetitorsLoading(true);
    try {
      const result = await fetchCompetitors(cleaned);
      if (result.niches.length || result.competitors.length) {
        setCompetitorsNiches(result.niches);
        setCompetitorsList(result.competitors);
        setCompetitorsNicheTitle(result.nicheTitle || '');
        setCompetitorsTotalFound(result.totalFound || result.competitors.length);
        setCompetitorsLoadedForAsin(cleaned);
        setCompetitorModalOpen(true);
      } else {
        message.info(t('idea_calc.calculator.msg_no_competitors'));
      }
    } catch {
      message.error(t('idea_calc.calculator.msg_competitors_load_error'));
    } finally {
      setCompetitorsLoading(false);
    }
  }, [asin, competitorsLoadedForAsin, competitorsNiches.length, competitorsList.length, t]);

  const handleCompetitorSelect = useCallback((selectedAsins: string[]) => {
    isDirtyRef.current = true;
    // Add ASINs
    setCompetitorAsins((prev) => {
      const newAsins = selectedAsins.filter((a) => !prev.includes(a));
      return [...prev, ...newAsins];
    });
    // Add full competitor data
    setSelectedCompetitors((prev) => {
      const existingAsins = new Set(prev.map((c) => c.asin));
      const newCompetitors = competitorsList.filter(
        (c) => selectedAsins.includes(c.asin) && !existingAsins.has(c.asin)
      );
      return [...prev, ...newCompetitors];
    });
    message.success(t('idea_calc.calculator.msg_competitors_added', { count: selectedAsins.length }));
  }, [message, competitorsList, t]);

  const addManualCompetitor = useCallback(() => {
    const val = manualCompetitorAsin.trim().toUpperCase();
    if (/^[A-Z0-9]{10}$/.test(val) && !competitorAsins.includes(val) && val !== asin) {
      isDirtyRef.current = true;
      setCompetitorAsins((prev) => [...prev, val]);
      setManualCompetitorAsin('');
    }
  }, [manualCompetitorAsin, competitorAsins, asin]);

  const removeCompetitor = useCallback((asinToRemove: string) => {
    isDirtyRef.current = true;
    setCompetitorAsins((prev) => prev.filter((a) => a !== asinToRemove));
    setSelectedCompetitors((prev) => prev.filter((c) => c.asin !== asinToRemove));
  }, []);

  // Calculate competitor metrics using main product's costs as approximation
  const calcCompetitorMetrics = useCallback((price: number) => {
    if (!price || price <= 0) return { referralFee: 0, profit: 0, margin: 0 };
    const referralFeeAmount = price * (data.referralFee / 100);
    // Profit = Price - Referral - FBA - COGS - Storage
    const profit = price - referralFeeAmount - data.fbaFee - cogs - data.storageFee;
    const margin = (profit / price) * 100;
    return { referralFee: referralFeeAmount, profit, margin };
  }, [data.referralFee, data.fbaFee, data.storageFee, cogs]);

  const competitorColumns: ColumnsType<CompetitorData> = React.useMemo(() => [
    {
      title: 'ASIN',
      dataIndex: 'asin',
      key: 'asin',
      render: (asin: string) => <Typography.Text copyable={{ text: asin }} style={{ fontSize: 10 }}>{asin}</Typography.Text>,
    },
    {
      title: t('idea_calc.calculator.competitor_col_price'),
      dataIndex: 'price',
      key: 'price',
      width: 60,
      render: (price: number) => <span style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{price ? `$${price.toFixed(2)}` : '—'}</span>,
    },
    {
      title: t('idea_calc.calculator.competitor_col_referral'),
      key: 'referral',
      width: 50,
      render: (_: unknown, record: CompetitorData) => {
        const { referralFee } = calcCompetitorMetrics(record.price || 0);
        return <span style={{ fontSize: 10, color: c.textSecondary }}>${referralFee.toFixed(2)}</span>;
      },
    },
    {
      title: t('idea_calc.calculator.competitor_col_profit'),
      key: 'profit',
      width: 55,
      render: (_: unknown, record: CompetitorData) => {
        const { profit } = calcCompetitorMetrics(record.price || 0);
        const color = profit >= 0 ? c.success : c.error;
        return <span style={{ fontSize: 10, fontWeight: 600, color }}>${profit.toFixed(2)}</span>;
      },
    },
    {
      title: t('idea_calc.calculator.competitor_col_margin'),
      key: 'margin',
      width: 50,
      render: (_: unknown, record: CompetitorData) => {
        const { margin } = calcCompetitorMetrics(record.price || 0);
        const color = margin >= 0 ? c.success : c.error;
        return <span style={{ fontSize: 10, fontWeight: 500, color }}>{margin.toFixed(0)}%</span>;
      },
    },
    {
      title: '⭐',
      dataIndex: 'rating',
      key: 'rating',
      width: 35,
      render: (rating: string | number) => <span style={{ fontSize: 10 }}>{rating || '—'}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 28,
      render: (_: unknown, record: CompetitorData) => (
        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeCompetitor(record.asin)} />
      ),
    },
  ], [calcCompetitorMetrics, removeCompetitor, t, c]);

  const vg: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6 };
  const unit: React.CSSProperties = { fontSize: 10, flexShrink: 0, lineHeight: 1, minWidth: 14, whiteSpace: 'nowrap' };

  return (
    <Card title={t('idea_calc.calculator.title')} variant="outlined" style={{ height: '100%', flex: 1 }}>
      <div className="calc-inputs" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* ASIN */}
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>ASIN</Typography.Text>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Input
              size="small"
              style={{ flex: 1 }}
              value={asin}
              placeholder="B0XXXXXXXX"
              maxLength={10}
              onChange={(e) => handleAsinChange(e.target.value)}
              onPressEnter={() => { if (asin.length === 10) lookupAsin(asin); }}
            />
            <Button
              size="small"
              type="primary"
              onClick={() => lookupAsin(asin)}
              disabled={asin.length !== 10 || keepaLoading}
              loading={keepaLoading}
            >
              Keepa
            </Button>
            <Tooltip title={t('idea_calc.calculator.open_amazon_tooltip')}>
              <Button
                size="small"
                icon={<AmazonOutlined />}
                disabled={asin.length !== 10}
                onClick={() => window.open(`https://www.amazon.com/dp/${asin}`, '_blank')}
              />
            </Tooltip>
            {keepaLoaded && <CheckCircleOutlined style={{ color: c.success, fontSize: 14 }} />}
          </div>
        </div>

        {/* Competitors */}
        <div style={{ background: c.fillSubtle, borderRadius: 6, padding: 6, border: `1px solid ${c.borderSubtle}` }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
            <TeamOutlined style={{ color: c.info }} />
            <Typography.Text strong style={{ fontSize: 11 }}>{t('idea_calc.calculator.competitors_label')}</Typography.Text>
            {competitorAsins.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 'auto', fontSize: 10 }}>{t('idea_calc.calculator.competitors_count_short', { count: competitorAsins.length })}</Tag>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
            <Input
              size="small"
              placeholder={t('idea_calc.calculator.competitor_asin_placeholder')}
              maxLength={10}
              style={{ flex: 1 }}
              value={manualCompetitorAsin}
              onChange={(e) => setManualCompetitorAsin(sanitizeAsin(e.target.value))}
              onPressEnter={addManualCompetitor}
            />
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={addManualCompetitor}
              disabled={!/^[A-Z0-9]{10}$/.test(manualCompetitorAsin.trim())}
            />
            <Button
              size="small"
              icon={competitorsLoading ? <Spin size="small" /> : <TeamOutlined />}
              onClick={loadCompetitors}
              disabled={!asin || asin.length !== 10 || competitorsLoading}
            >
              {t('idea_calc.calculator.auto_button')}
            </Button>
          </div>
          {competitorAsins.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {competitorAsins.map((ca) => (
                <Tag
                  key={ca}
                  closable
                  onClose={() => removeCompetitor(ca)}
                  style={{ fontSize: 10, margin: 0 }}
                >
                  {ca}
                </Tag>
              ))}
            </div>
          )}
          {selectedCompetitors.length > 0 && (
            <Table
              className="competitor-table"
              dataSource={selectedCompetitors}
              columns={competitorColumns}
              rowKey="asin"
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
            />
          )}
        </div>

        {/* Себестоимость */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calculator.section_cogs')}</Typography.Text>
        <Row gutter={[8, 6]}>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_purchase_cny')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.1} controls={false} value={purchasePriceCny} onChange={handleCnyChange} placeholder="CNY" {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>¥</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_purchase_usd')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.purchasePrice} onChange={handleUsdChange} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_shipping')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.shippingCostPerKg} onChange={(v) => handleChange('shippingCostPerKg', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$/{t('idea_calc.calculator.unit_kg')}</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>
              {t('idea_calc.calculator.field_weight')} {weightFromKeepa && <Tag color="blue" style={{ fontSize: 9, lineHeight: '14px', padding: '0 3px', margin: 0, verticalAlign: 'middle' }}>K</Tag>}
            </Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0.01} step={0.01} controls={false} value={data.unitWeightKg} onChange={(v) => handleChange('unitWeightKg', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_kg')}</Typography.Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[8, 6]}>
          <Col span={8}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_storage_fee')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.storageFee} onChange={(v) => handleChange('storageFee', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[8, 6]}>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>
              {t('idea_calc.calculator.field_length')} {dimsFromKeepa && <Tag color="blue" style={{ fontSize: 9, lineHeight: '14px', padding: '0 3px', margin: 0, verticalAlign: 'middle' }}>K</Tag>}
            </Typography.Text>
            <div style={vg}>
              <InputNumber
                size="small"
                style={{ flex: 1 }}
                min={0.1}
                step={0.1}
                controls={false}
                value={dims.l}
                placeholder="L"
                onChange={(v) => handleDimChange('l', v)}
                {...numericInputProps}
              />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_in')}</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_width')}</Typography.Text>
            <div style={vg}>
              <InputNumber
                size="small"
                style={{ flex: 1 }}
                min={0.1}
                step={0.1}
                controls={false}
                value={dims.w}
                placeholder="W"
                onChange={(v) => handleDimChange('w', v)}
                {...numericInputProps}
              />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_in')}</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_height')}</Typography.Text>
            <div style={vg}>
              <InputNumber
                size="small"
                style={{ flex: 1 }}
                min={0.1}
                step={0.1}
                controls={false}
                value={dims.h}
                placeholder="H"
                onChange={(v) => handleDimChange('h', v)}
                {...numericInputProps}
              />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_in')}</Typography.Text>
            </div>
          </Col>
          <Col span={6}>
            <Typography.Text style={{ fontSize: 11, color: c.textTertiary, whiteSpace: 'nowrap' }}>{t('idea_calc.calculator.field_volumetric_weight')}</Typography.Text>
            <div style={vg}>
              <InputNumber
                size="small"
                style={{ flex: 1, background: c.fillMuted }}
                readOnly
                controls={false}
                value={volumetricWeightKg}
                placeholder="—"
              />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_kg')}</Typography.Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[8, 6]}>
          <Col span={8}>
            <Typography.Text style={{ fontSize: 11, color: volumetricWeightKg != null && volumetricWeightKg > data.unitWeightKg ? c.info : c.textTertiary }}>
              {t('idea_calc.calculator.field_billable_weight')}
            </Typography.Text>
            <div style={vg}>
              <InputNumber
                size="small"
                style={{
                  flex: 1,
                  background: volumetricWeightKg != null && volumetricWeightKg > data.unitWeightKg ? c.infoBg : c.fillMuted,
                }}
                readOnly
                controls={false}
                value={+billableWeightKg.toFixed(3)}
              />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_kg')}</Typography.Text>
            </div>
          </Col>
          <Col span={8}>
            <Typography.Text style={{ fontSize: 11, color: c.textTertiary }}>{t('idea_calc.calculator.field_shipping_per_unit')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1, background: c.fillMuted }} readOnly controls={false} value={shippingPrice} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[8, 6]}>
          <Col span={8}>
            <Typography.Text style={{ fontSize: 11, color: c.textTertiary }}>{t('idea_calc.calculator.field_cogs')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1, background: c.fillMuted }} readOnly controls={false} value={+cogs.toFixed(2)} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
        </Row>

        {/* Цена и комиссии */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calculator.section_price_fees')}</Typography.Text>
        <Row gutter={[8, 6]}>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_price')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.sellingPrice} onChange={handleSellingPriceChange} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_referral')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} max={100} step={0.1} controls={false} value={data.referralFee} onChange={(v) => handleChange('referralFee', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>%</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_fba')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.fbaFee} onChange={(v) => handleChange('fbaFee', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_other_fees')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} max={100} step={0.1} controls={false} value={data.otherFees} onChange={(v) => handleChange('otherFees', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>%</Typography.Text>
            </div>
          </Col>
        </Row>

        {/* Реклама и возвраты */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calculator.section_ads_returns')}</Typography.Text>
        <Row gutter={[8, 6]}>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_cpc')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={0.01} controls={false} value={data.ppcCost} onChange={(v) => handleChange('ppcCost', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>$</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_conversion')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0.01} max={100} step={0.1} controls={false} value={data.conversionRate} onChange={(v) => handleChange('conversionRate', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>%</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_ppc_sales')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} max={100} step={1} controls={false} value={data.ppcSalesPercentage} onChange={(v) => handleChange('ppcSalesPercentage', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>%</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_returns')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} max={100} step={0.1} controls={false} value={data.returnRate} onChange={(v) => handleChange('returnRate', v)} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>%</Typography.Text>
            </div>
          </Col>
        </Row>

        {/* Продажи и сток */}
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>{t('idea_calc.calculator.section_sales_stock')}</Typography.Text>
        <Row gutter={[8, 6]}>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_monthly_sales')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={0} step={1} controls={false} value={data.monthlySales} onChange={handleMonthlySalesChange} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_pcs')}</Typography.Text>
            </div>
          </Col>
          <Col span={12}>
            <Typography.Text style={{ fontSize: 11 }}>{t('idea_calc.calculator.field_batch')}</Typography.Text>
            <div style={vg}>
              <InputNumber size="small" style={{ flex: 1 }} min={1} step={1} controls={false} value={data.batchQuantity} onChange={handleBatchChange} {...numericInputProps} />
              <Typography.Text type="secondary" style={unit}>{t('idea_calc.calculator.unit_pcs')}</Typography.Text>
            </div>
          </Col>
        </Row>

        {/* Купон/Промо */}
        <div style={{ marginTop: 4, padding: '6px 8px', background: couponPromoEnabled ? c.warningBg : c.fillSubtle, borderRadius: 6, border: `1px solid ${couponPromoEnabled ? c.warningBorder : c.borderSubtle}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GiftOutlined style={{ color: couponPromoEnabled ? c.warning : c.textTertiary }} />
            <Typography.Text strong style={{ fontSize: 11, flex: 1 }}>{t('idea_calc.calculator.coupon_promo_title')}</Typography.Text>
            <Switch size="small" checked={couponPromoEnabled} onChange={handleCouponPromoChange} />
          </div>
          <Tooltip title={t('idea_calc.calculator.coupon_promo_formula')}>
            <Typography.Text type="secondary" style={{ fontSize: 9, display: 'block', marginTop: 4 }}>
              {couponPromoEnabled
                ? t('idea_calc.calculator.coupon_promo_enabled_hint')
                : t('idea_calc.calculator.coupon_promo_disabled_hint')}
            </Typography.Text>
          </Tooltip>
        </div>

        {/* Exchange Rate */}
        <div style={{ marginTop: 4, padding: '4px 8px', background: c.fillSubtle, borderRadius: 6, border: `1px solid ${c.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>1 USD =</Typography.Text>
          {rateLoading ? (
            <Spin size="small" />
          ) : (
            <InputNumber
              size="small"
              min={1}
              max={20}
              step={0.01}
              controls={false}
              value={cnyRate}
              onChange={handleRateChange}
              style={{ width: 60, fontSize: 10 }}
              {...numericInputProps}
            />
          )}
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>{t('idea_calc.calculator.exchange_rate_unit')}</Typography.Text>
        </div>
      </div>
      <CompetitorModal
        open={competitorModalOpen}
        niches={competitorsNiches}
        competitors={competitorsList}
        loading={competitorsLoading}
        onClose={() => setCompetitorModalOpen(false)}
        onSelect={handleCompetitorSelect}
        sourceAsin={asin}
        nicheTitle={competitorsNicheTitle}
        totalFound={competitorsTotalFound}
        alreadySelectedAsins={competitorAsins}
      />
    </Card>
  );
};

export default React.memo(Calculator);
